/**
 * PKCE for the Google sign-in redirect (QZ-D28): a random verifier, its SHA-256
 * challenge, and a `state`, round-tripped through `sessionStorage` across the
 * redirect to Cognito's `/oauth2/authorize` and back. Only the verifier and
 * `state` are ever persisted - never a token.
 */

/** Unpadded base64url, the encoding PKCE and its challenge both use. */
export const base64url = (bytes: Uint8Array): string => {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/** PKCE's S256: the base64url SHA-256 digest of the verifier's ASCII bytes. */
export const challengeFor = async (verifier: string): Promise<string> =>
  base64url(
    new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)))
  );

/** A fresh verifier/challenge pair for one sign-in attempt. */
export const createPkcePair = async (): Promise<{ verifier: string; challenge: string }> => {
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(64)));
  const challenge = await challengeFor(verifier);
  return { verifier, challenge };
};

/** A random `state`, checked against the one Cognito's redirect echoes back. */
export const createState = (): string => base64url(crypto.getRandomValues(new Uint8Array(16)));

/** What `startGoogleSignIn` leaves for `completeGoogleSignIn` to pick up. */
export interface PendingSignIn {
  verifier: string;
  state: string;
  redirectUri: string;
}

/** The one `sessionStorage` key a sign-in in flight lives under. */
export const PENDING_SIGN_IN_KEY = 'quizdb.auth.pendingSignIn';

export const savePendingSignIn = (
  pending: PendingSignIn,
  storage: Storage = sessionStorage
): void => {
  storage.setItem(PENDING_SIGN_IN_KEY, JSON.stringify(pending));
};

/** Reads the pending entry, parsing defensively: anything malformed reads as none. */
export const loadPendingSignIn = (storage: Storage = sessionStorage): PendingSignIn | undefined => {
  const raw = storage.getItem(PENDING_SIGN_IN_KEY);
  if (raw === null) return undefined;
  try {
    const value = JSON.parse(raw) as unknown;
    if (typeof value !== 'object' || value === null) return undefined;
    const { verifier, state, redirectUri } = value as Record<string, unknown>;
    if (
      typeof verifier !== 'string' ||
      typeof state !== 'string' ||
      typeof redirectUri !== 'string'
    ) {
      return undefined;
    }
    return { verifier, state, redirectUri };
  } catch {
    return undefined;
  }
};

export const clearPendingSignIn = (storage: Storage = sessionStorage): void => {
  storage.removeItem(PENDING_SIGN_IN_KEY);
};
